import React from "react";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../requests/shared/helpers/DrawerFooterContainerLarge";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postRightworkAPI } from "../../requests/utilities/requests";
import {
  API_post_logbook_agent,
  API_delete_logbook_agent,
  API_post_run_logbook_agent,
} from "../../../../constants";
import {
  DrawerLabel,
  DrawerSaveFooter,
  INPUT_SX,
  MULTILINE_SX,
} from "./drawerInputs";

// Creates or edits a logbook AI agent: name, prompt, and the explicit source
// allowlist. Runs automatically with the morning email; "Run test on
// yesterday" previews the output right here before anything ships.
function AgentBlockDrawer(props) {
  const [name, setName] = React.useState("");
  const [prompt, setPrompt] = React.useState("");
  const [testOutput, setTestOutput] = React.useState(null);
  const [isTesting, setIsTesting] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const isEdit = Boolean(props.agent);

  React.useEffect(() => {
    if (props.open) {
      setName(props.agent?.name || "");
      setPrompt(props.agent?.prompt || "");
      setTestOutput(null);
    }
  }, [props.open, props.agent]);

  // Persists the drawer's current draft; shared by Save and Run test so a
  // test always exercises the configuration visible on screen, never a stale
  // saved version.
  const persistAgent = async () => {
    if (!name.trim() || !prompt.trim()) {
      toast.error("Name and prompt are required", { autoClose: 3000 });
      return null;
    }
    // Snapshot for the callback: the builder guards mutation completions
    // against a mid-flight company switch.
    const requestCompany = props.homeCompany;
    try {
      const res = await postRightworkAPI(API_post_logbook_agent, {
        company_id: requestCompany,
        ...(props.agent?.uuid && { agent_uuid: props.agent.uuid }),
        name: name.trim(),
        prompt: prompt.trim(),
      });
      if (res.status === 200) {
        const agent = res.data?.redux?.payload?.agent;
        props.onSaved(agent, isEdit, requestCompany);
        return agent;
      }
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "Request Failed",
        { autoClose: 3000 }
      );
    }
    return null;
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const agent = await persistAgent();
      if (agent) {
        toast.success("Agent saved", { autoClose: 3000 });
        props.onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const archiveAgent = async () => {
    const confirmed = window.confirm(
      `Archive the agent "${props.agent?.name}"? Its block comes off the layout.`
    );
    if (!confirmed) return;
    try {
      const res = await postRightworkAPI(API_delete_logbook_agent, {
        company_id: props.homeCompany,
        agent_uuid: props.agent.uuid,
      });
      if (res.status === 200) {
        toast.success("Agent archived", { autoClose: 3000 });
        props.onArchived(props.agent);
        props.onClose();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "Request Failed",
        { autoClose: 3000 }
      );
    }
  };

  const runTest = async () => {
    if (!props.agent?.uuid) {
      toast.error("Save the agent first, then run a test", { autoClose: 3000 });
      return;
    }
    setIsTesting(true);
    setTestOutput(null);
    try {
      // Persist the draft first: the test endpoint runs (and overwrites
      // yesterday's stored output with) the SAVED configuration, so testing
      // an unsaved edit would exercise the old prompt and poison the stored
      // run with its result.
      const agent = await persistAgent();
      if (!agent) {
        return;
      }
      const res = await postRightworkAPI(API_post_run_logbook_agent, {
        store_id: props.homeStore,
        agent_uuid: agent.uuid,
        // No date: the server derives "yesterday" in the STORE's timezone.
        // Computing it here would use the browser timezone, which can pick
        // the wrong business date around midnight and overwrite that day's
        // stored run.
      });
      if (res.status === 200) {
        setTestOutput(res.data?.redux?.payload?.run?.output_text || "(empty)");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "The test run failed",
        { autoClose: 3000 }
      );
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose}>
      <div className="w-[500px] h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader
            title={isEdit ? "Edit AI Agent" : "Add AI Agent"}
            onClose={props.onClose}
          />
          <div className="w-full px-6">
            <p className="text-slate-500 text-sm -mt-6">
              Runs with the morning email · regenerate any time from the Daily
              tab. The agent looks up whatever your instructions ask for.
            </p>

            <div className="mt-7">
              <DrawerLabel>Name</DrawerLabel>
              <div className="mt-1">
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Service Recap"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  InputProps={{ sx: INPUT_SX }}
                />
              </div>
            </div>

            <div className="mt-7">
              <DrawerLabel>Prompt</DrawerLabel>
              <div className="mt-1">
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={6}
                  placeholder="Summarize yesterday's service for this store in 3 to 5 sentences. Call out anything mentioned more than once this week, any guest recovery that needs follow-up, and whether sales and labor hit their targets. Be direct, no fluff."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  InputProps={{ sx: MULTILINE_SX }}
                />
              </div>
            </div>

            {isEdit && (
              <div className="mt-7">
                <LoadingButton
                  loading={isTesting}
                  loadingPosition="end"
                  endIcon={<PlayArrowIcon />}
                  variant="outlined"
                  onClick={runTest}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    borderColor: "#6d28d9",
                    color: "#6d28d9",
                    "&:hover": {
                      borderColor: "#5b21b6",
                      backgroundColor: "#f5f3ff",
                    },
                  }}
                >
                  Save &amp; test on yesterday
                </LoadingButton>
                {testOutput && (
                  <div className="mt-3 bg-white border border-violet-200 rounded-xl p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-1">
                      Test output
                    </p>
                    <p className="text-sm text-slate-700 whitespace-pre-line">
                      {testOutput}
                    </p>
                  </div>
                )}
              </div>
            )}

            {isEdit && (
              <div className="mt-7">
                <p
                  className="text-sm font-medium text-rose-600 cursor-pointer hover:text-rose-800"
                  onClick={archiveAgent}
                >
                  Archive this agent
                </p>
              </div>
            )}
          </div>
          <DrawerFooterContainerLarge>
            <DrawerSaveFooter onSave={save} onClose={props.onClose} saving={isSaving} disabled={!name.trim() || !prompt.trim()} />
          </DrawerFooterContainerLarge>
          <div className="h-[200px]"></div>
        </div>
      </div>
    </Drawer>
  );
}

export default AgentBlockDrawer;
